/**
 * Typed client for the engineering side of Process Control (docs/control/API.md, apps/api/src/control/*): releases,
 * interlocks and the override register, the simulation view and fault injection. Every payload is for a SIMULATED plant.
 */
import { api } from "./api.js";

const P = "/api/v1/control";

export type Quality = "GOOD" | "UNCERTAIN" | "BAD";

export interface ClockInfo {
  mode: "REALTIME" | "ACCELERATED";
  factor: number;
  runClass: string;
  gmpLinked: boolean;
  simulated: boolean;
  simTimeSeconds: number;
}
export interface ControlHealth {
  state: "RUNNING" | "STOPPED" | "NOT_LOADED" | "REFUSED" | "ENGINE_STOPPED";
  release: { id: string; no: number; hash: string; activatedAt: string } | null;
  clock: ClockInfo;
}
export interface TagValue {
  unit: string;
  tag: string;
  value: number | boolean;
  status: Quality;
  eu: string | null;
}
export interface ControlSnapshot {
  state: string;
  release: ControlHealth["release"];
  clock: ClockInfo;
  tags: TagValue[];
}

/** A block of a module as the engine config holds it: id and kind, plus whatever parameters the kind has. */
export interface BlockCfg {
  kind: string;
  id: string;
  [param: string]: unknown;
}
export interface AlarmCfg {
  id: string;
  input: string;
  type: string;
  limit?: number;
  deadband?: number;
  priority: number;
  suppressWhen?: string;
  consequence?: string;
  response?: string;
}
export interface ModuleCfg {
  id: string;
  periodMs: number;
  blocks: BlockCfg[];
  alarms?: AlarmCfg[];
}
export interface HistorianCfg {
  tag: string;
  deadband: number;
  eu?: string;
  heartbeatS?: number;
}
export interface CommandCfg {
  id: string;
  label: string;
  tag: string;
  value: number | boolean;
  permissives: { id: string; text: string; expr: string }[];
}
export interface UnitCfg {
  id: string;
  plant: string;
  modules: ModuleCfg[];
  historian: HistorianCfg[];
  signedWrites: string[];
  commands: CommandCfg[];
}
export interface ReleaseContent {
  schema: 1;
  units: UnitCfg[];
}

export type ReleaseStatus = "DRAFT" | "SUBMITTED" | "APPROVED" | "REJECTED" | "SEEDED";
export interface ReleaseRow {
  id: string;
  release_no: number;
  content_hash: string;
  status: ReleaseStatus;
  running: boolean;
  reason: string;
  change_control_ref: string;
  prepared_by: string;
  prepared_by_name: string;
  prepared_at_server: string;
  parent_release_id: string | null;
  decided_by_name: string | null;
  decision_reason: string | null;
  decided_at_server: string | null;
  signature_id: string | null;
}
export interface ReleaseDetail extends ReleaseRow {
  content: ReleaseContent;
}
export interface DiffEntry {
  path: string;
  change: "added" | "removed" | "changed";
  before?: unknown;
  after?: unknown;
}
/** One plain-language change (apps/api release-diff-text.ts); `raw` are the JSON entries behind it. */
export interface DiffLine {
  kind: DiffEntry["change"];
  text: string;
  /** A change a reviewer must look at twice (a removed signed write). */
  warning: boolean;
  raw: DiffEntry[];
}
export interface DiffGroup {
  unit: string;
  module: string | null;
  lines: DiffLine[];
}
export interface ReleaseDiff {
  from: { id: string; releaseNo: number; hash: string };
  to: { id: string; releaseNo: number; hash: string };
  changes: DiffEntry[];
  groups: DiffGroup[];
}
export type Validation = { valid: true; hash: string } | { valid: false; error: string };

export interface InterlockRow {
  unit: string;
  id: string;
  latch: boolean;
  conditions: { id: string; expr: string; text?: string }[];
  ok: boolean;
  tripped: boolean;
  bypassed: boolean;
  failing: number;
  quality: Quality;
  devices: string[];
  firstOut: string | null;
  trippedAt: string | null;
}
export interface OverrideRow {
  unit: string;
  interlock: string;
  condition: string;
  requestedBy: string;
  signedBy: string;
  signatureId: string | null;
  reason: string | null;
  riskRef: string | null;
  startedAt: string;
  expiresAt: string | null;
  remainingSeconds: number | null;
  state: "ACTIVE" | "EXPIRED" | "REMOVED";
  endedAt: string | null;
  endedBy: string | null;
  training: boolean;
}
/** An override somebody asked for that a second person with the override permission has not yet approved. */
export interface PendingOverride {
  requestId: string;
  unit: string;
  tag: string;
  interlock: string;
  condition: string;
  durationMin: number;
  riskRef: string;
  reason: string;
  requestedBy: string;
  requestedById: string;
  requestedAt: string;
  approveBy: string;
}
export interface OverrideRegister {
  active: OverrideRow[];
  history: OverrideRow[];
}

export interface FaultTarget {
  unit: string;
  target: string;
  kinds: string[];
  active: { kind: string; driftPerSecond?: number; injectedAt: string; injectedBy: string } | null;
}
export interface SimulationView {
  state: string;
  clock: ClockInfo;
  gmpLinked: boolean;
  faultInjectionLocked: boolean;
  units: { unit: string; plant: string; market: string | null; title: string }[];
  targets: FaultTarget[];
}

/** The second step of a signed request (an override, a signed write): who must sign what. */
export interface SigningInfo {
  requestId: string;
  subjectType: "control_request";
  subjectId: string;
  meaning: "PERFORMED";
}

// ---------------------------------------------------------------- plant model (URS-CTL-001)
export type PlantLevelId = "SITE" | "AREA" | "PROCESS_CELL" | "UNIT" | "EQUIPMENT_MODULE" | "CONTROL_MODULE";
export interface PlantObjectRow {
  id: string;
  tag: string;
  label: string;
  level: PlantLevelId;
  parentTag: string | null;
  equipmentCode: string | null;
}
export interface PlantChangeRow {
  id: string;
  kind: "CREATE" | "RENAME" | "MOVE" | "LINK";
  tag: string;
  newTag: string | null;
  label: string | null;
  level: PlantLevelId | null;
  parentTag: string | null;
  equipmentCode: string | null;
  reason: string;
  by: string;
  at: string;
}
export interface PlantView {
  running: PlantObjectRow[];
  draft: PlantObjectRow[];
  pending: PlantChangeRow[];
  retiredTags: string[];
}
export type PlantProposal =
  | {
      kind: "CREATE";
      tag: string;
      label: string;
      level: PlantLevelId;
      parentTag: string | null;
      equipmentCode?: string | null;
      reason: string;
    }
  | { kind: "RENAME"; tag: string; newTag?: string; label?: string; reason: string }
  | { kind: "MOVE"; tag: string; parentTag: string | null; reason: string }
  | { kind: "LINK"; tag: string; equipmentCode: string; reason: string };

// ---------------------------------------------------------------- templates (URS-CTL-007)
export interface TemplateInfo {
  market: string;
  id: string;
  label: string;
  version: number;
  members: { role: string; prefix: string; description: string }[];
  loops: number;
}
export interface TemplateCatalogue {
  templates: TemplateInfo[];
  plants: { unit: string; kind: string; title: string; market: string }[];
}

// ---------------------------------------------------------------- training runs (URS-CTL-014)
export interface TrainingScenarioInfo {
  id: string;
  label: string;
  unit: string;
  description: string;
  durationS: number;
  faults: { atS: number; target: string; kind: string }[];
  expected: { id: string; text: string }[];
}
export interface Trainee {
  id: string;
  name: string;
  qualifications: { curriculum: string; status: string; expiresAt: string | null }[];
}
export interface TrainingRunView {
  training: true;
  simulated: true;
  run: {
    id: string;
    unit: string;
    scenario: { id: string; label: string; description: string } | null;
    trainee: Trainee;
    instructor: string;
    startedAt: string;
    state: "RUNNING" | "PAUSED" | "ENDED";
    simMs: number;
  };
  tags: { tag: string; value: number | boolean; status: Quality }[];
  alarms?: {
    unit: string;
    id: string;
    state: string;
    priority: number;
    consequence: string | null;
    response: string | null;
    awaitingAck: boolean;
  }[];
  faults: { target: string; kind: string }[];
  score: {
    percent: number;
    results: { id: string; text: string; met: boolean; responseS: number | null }[];
  } | null;
  events: {
    seq: number;
    kind: string;
    simMs: number;
    at: string;
    by: string;
    data: Record<string, unknown>;
    training: true;
  }[];
}
export interface TrainingRunRow {
  id: string;
  scenarioId: string | null;
  unit: string;
  trainee: string;
  startedAt: string;
  training: true;
  ended?: boolean;
}
export type WriteResult =
  | { applied: true; tag: string }
  | { applied: false; signatureRequired: true; signing: SigningInfo };

export interface SignBody {
  payloadHash: string;
  idToken: string;
}

/** The reason typed before the step-up redirect; read once when the signature is applied (it is not a secret). */
export const ACTIVATION_REASON_KEY = "ebr.control.activation.reason";

export const controlEng = {
  health: () => api.get<ControlHealth>(`${P}/health`),
  snapshot: () => api.get<ControlSnapshot>(`${P}/snapshot`),
  releases: () => api.get<ReleaseRow[]>(`${P}/releases`),
  release: (id: string) => api.get<ReleaseDetail>(`${P}/releases/${id}`),
  diff: (id: string, against = "running") =>
    api.get<ReleaseDiff>(`${P}/releases/${id}/diff?against=${encodeURIComponent(against)}`),
  validate: (content: unknown) => api.post<Validation>(`${P}/releases/validate`, { content }),
  createRelease: (b: {
    content: unknown;
    reason: string;
    changeControlRef: string;
    parentReleaseId?: string;
  }) => api.post<ReleaseDetail>(`${P}/releases`, b),
  submit: (id: string) => api.post<ReleaseDetail>(`${P}/releases/${id}/submit`),
  approve: (id: string, b: SignBody) => api.post<ReleaseDetail>(`${P}/releases/${id}/approve`, b),
  reject: (id: string, reason: string) => api.post<ReleaseDetail>(`${P}/releases/${id}/reject`, { reason }),
  /** The download is a signed act (PERFORMED, bound to the release hash): the reason and the signature go together. */
  activate: (id: string, b: SignBody & { reason: string }) =>
    api.post<ReleaseDetail>(`${P}/releases/${id}/activate`, b),
  interlocks: () => api.get<{ interlocks: InterlockRow[] }>(`${P}/interlocks`),
  overrides: () => api.get<OverrideRegister>(`${P}/overrides`),
  requestOverride: (b: {
    interlock: string;
    condition: string;
    durationMin: number;
    reason: string;
    riskRef: string;
  }) => api.post<WriteResult & { signing?: SigningInfo }>(`${P}/overrides`, b),
  pendingOverrides: () => api.get<PendingOverride[]>(`${P}/overrides/requests`),
  removeOverride: (b: { interlock: string; condition: string }) =>
    api.post<{ removed: true }>(`${P}/overrides/remove`, b),
  write: (b: { tag: string; value: number | boolean; reason?: string }) =>
    api.post<WriteResult>(`${P}/writes`, b),
  signRequest: (id: string, b: SignBody) => api.post<unknown>(`${P}/requests/${id}/sign`, b),
  simulation: () => api.get<SimulationView>(`${P}/simulation`),
  injectFault: (b: { unit: string; target: string; kind: string; driftPerSecond?: number }) =>
    api.post<SimulationView>(`${P}/simulation/faults`, b),
  clearFault: (b: { unit: string; target: string }) =>
    api.post<SimulationView>(`${P}/simulation/faults/clear`, b),
  plant: () => api.get<PlantView>(`${P}/plant`),
  proposePlant: (b: PlantProposal) => api.post<PlantView>(`${P}/plant/changes`, b),
  plantRelease: (b: { reason: string; changeControlRef: string }) =>
    api.post<{ release: ReleaseDetail; changes: number }>(`${P}/plant/release`, b),
  templates: () => api.get<TemplateCatalogue>(`${P}/templates`),
  moduleFromTemplate: (b: {
    market: string;
    templateId: string;
    number: string;
    name?: string;
    unit: string;
    reason: string;
    changeControlRef: string;
  }) => api.post<ReleaseDetail>(`${P}/modules/from-template`, b),
  scenarios: () => api.get<{ scenarios: TrainingScenarioInfo[] }>(`${P}/training/scenarios`),
  trainees: () => api.get<Trainee[]>(`${P}/training/trainees`),
  trainingRuns: () => api.get<TrainingRunRow[]>(`${P}/training/runs`),
  trainingRun: (id: string) => api.get<TrainingRunView>(`${P}/training/runs/${id}`),
  startRun: (b: { traineeId: string; scenarioId?: string; unit: string }) =>
    api.post<TrainingRunView>(`${P}/training/runs`, b),
  runControl: (id: string, action: "pause" | "resume" | "reset") =>
    api.post<TrainingRunView>(`${P}/training/runs/${id}/${action}`),
  runAdvance: (id: string, seconds: number) =>
    api.post<TrainingRunView>(`${P}/training/runs/${id}/advance`, { seconds }),
  runEnd: (id: string, b: { outcome: "PASSED" | "NEEDS_REPEAT" | "ABANDONED"; note: string }) =>
    api.post<TrainingRunView>(`${P}/training/runs/${id}/end`, b),
  runFault: (id: string, b: { target: string; kind: string; driftPerSecond?: number; clear?: boolean }) =>
    api.post<TrainingRunView>(`${P}/training/runs/${id}/faults`, b),
  runAct: (id: string, b: { tag: string; value: number | boolean }) =>
    api.post<TrainingRunView>(`${P}/training/runs/${id}/actions`, b),
};
